import React, { useState } from 'react';
import { ArrowLeftIcon, SearchIcon, MoreVerticalIcon, SendIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Avatar from '../components/shared/Avatar';
interface ChatMessage {
  id: string;
  text: string;
  sender: 'user' | 'contact';
  timestamp: string;
  read: boolean;
}
interface ChatContact {
  id: string;
  name: string;
  username: string;
  avatar: string;
  verified: boolean;
  lastMessage: string;
  lastMessageTime: string;
  unreadCount: number;
  online: boolean;
  role: string;
  sport?: string;
}
const ChatPage = () => {
  const [activeTab, setActiveTab] = useState<'chats' | 'requests'>('chats');
  const [activeChat, setActiveChat] = useState<string | null>(null);
  const [messageInput, setMessageInput] = useState('');
  const contacts: ChatContact[] = [{
    id: '1',
    name: 'Cristiano Ronaldo',
    username: 'cristiano',
    avatar: 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61',
    verified: true,
    lastMessage: 'Looking forward to seeing you at the charity event!',
    lastMessageTime: '10:30 AM',
    unreadCount: 2,
    online: true,
    role: 'Player',
    sport: 'football'
  }, {
    id: '2',
    name: 'FC Barcelona',
    username: 'fcbarcelona',
    avatar: 'https://images.unsplash.com/photo-1522778119026-d647f0596c20',
    verified: true,
    lastMessage: 'Thank you for your support!',
    lastMessageTime: 'Yesterday',
    unreadCount: 0,
    online: true,
    role: 'Club',
    sport: 'football'
  }, {
    id: '3',
    name: 'Serena Williams',
    username: 'serenawilliams',
    avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80',
    verified: true,
    lastMessage: 'Great match yesterday! Your technique has improved a lot.',
    lastMessageTime: '2 days ago',
    unreadCount: 0,
    online: false,
    role: 'Player',
    sport: 'tennis'
  }];
  const messageRequests: ChatContact[] = [{
    id: '4',
    name: 'Nike',
    username: 'nike',
    avatar: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff',
    verified: true,
    lastMessage: 'We would like to discuss a potential sponsorship opportunity with you.',
    lastMessageTime: '3 days ago',
    unreadCount: 1,
    online: false,
    role: 'Sponsor'
  }, {
    id: '5',
    name: 'Real Madrid Scout',
    username: 'rmscout',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d',
    verified: true,
    lastMessage: 'Impressed by your recent performance. Would like to arrange a meeting.',
    lastMessageTime: '1 week ago',
    unreadCount: 1,
    online: false,
    role: 'Scout',
    sport: 'football'
  }];
  const chatMessages: Record<string, ChatMessage[]> = {
    '1': [{
      id: '1-1',
      text: 'Hey! How are you doing?',
      sender: 'contact',
      timestamp: '10:15 AM',
      read: true
    }, {
      id: '1-2',
      text: "I'm good, thanks! Just finished training.",
      sender: 'user',
      timestamp: '10:20 AM',
      read: true
    }, {
      id: '1-3',
      text: 'Great to hear that. Looking forward to seeing you at the charity event!',
      sender: 'contact',
      timestamp: '10:30 AM',
      read: false
    }],
    '2': [{
      id: '2-1',
      text: 'Congratulations on your recent performance!',
      sender: 'contact',
      timestamp: 'Yesterday, 2:15 PM',
      read: true
    }, {
      id: '2-2',
      text: 'Thank you! It means a lot coming from FC Barcelona!',
      sender: 'user',
      timestamp: 'Yesterday, 3:30 PM',
      read: true
    }, {
      id: '2-3',
      text: 'Thank you for your support!',
      sender: 'contact',
      timestamp: 'Yesterday, 4:45 PM',
      read: true
    }]
  };
  const selectedContact = activeChat ? [...contacts, ...messageRequests].find(c => c.id === activeChat) : null;
  const handleSendMessage = () => {
    if (messageInput.trim() && activeChat) {
      // In a real app, you would send this to an API
      // For now, we'll just clear the input
      setMessageInput('');
    }
  };
  if (activeChat) {
    return <div className="bg-white min-h-full flex flex-col" data-id="element-589">
        {/* Chat Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 bg-white" data-id="element-590">
          <div className="flex items-center" data-id="element-591">
            <button className="text-gray-800 mr-3" onClick={() => setActiveChat(null)} data-id="element-592">
              <ArrowLeftIcon size={24} data-id="element-593" />
            </button>
            {selectedContact && <div className="flex items-center" data-id="element-594">
                <div className="relative" data-id="element-595">
                  <Avatar src={selectedContact.avatar} size="md" sport={selectedContact.sport} data-id="element-596" />
                  {selectedContact.online && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-597"></div>}
                </div>
                <div className="ml-3" data-id="element-598">
                  <div className="flex items-center" data-id="element-599">
                    <span className="font-semibold text-sm" data-id="element-600">
                      {selectedContact.name}
                    </span>
                    {selectedContact.verified && <svg className="w-3 h-3 text-blue-500 ml-1" fill="currentColor" viewBox="0 0 20 20" data-id="element-601">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-602"></path>
                      </svg>}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-603">
                    <span data-id="element-604">@{selectedContact.username}</span>
                    <span className="mx-1" data-id="element-605">•</span>
                    <span data-id="element-606">{selectedContact.role}</span>
                  </div>
                </div>
              </div>}
          </div>
          <button className="text-gray-800" data-id="element-607">
            <MoreVerticalIcon size={20} data-id="element-608" />
          </button>
        </div>
        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50" data-id="element-609">
          <div className="space-y-4" data-id="element-610">
            {activeChat && chatMessages[activeChat] ? chatMessages[activeChat].map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-611">
                  <div className={`max-w-[75%] rounded-lg px-4 py-2 ${message.sender === 'user' ? 'bg-blue-600 text-white rounded-br-none' : 'bg-gray-200 text-gray-800 rounded-bl-none'}`} data-id="element-612">
                    <p className="text-sm" data-id="element-613">{message.text}</p>
                    <div className={`text-xs mt-1 ${message.sender === 'user' ? 'text-blue-100' : 'text-gray-500'}`} data-id="element-614">
                      {message.timestamp}
                      {message.sender === 'user' && <span className="ml-1" data-id="element-615">
                          {message.read ? '✓✓' : '✓'}
                        </span>}
                    </div>
                  </div>
                </div>) : <div className="text-center text-gray-500 py-8" data-id="element-616">
                Start a conversation with {selectedContact?.name}
              </div>}
          </div>
        </div>
        {/* Message Input */}
        <div className="border-t border-gray-200 px-4 py-3 bg-white" data-id="element-617">
          <div className="flex items-center" data-id="element-618">
            <input type="text" placeholder="Type a message..." className="flex-1 border border-gray-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500" value={messageInput} onChange={e => setMessageInput(e.target.value)} onKeyPress={e => {
            if (e.key === 'Enter') {
              handleSendMessage();
            }
          }} data-id="element-619" />
            <button className="ml-2 bg-blue-600 text-white rounded-full p-2" onClick={handleSendMessage} disabled={!messageInput.trim()} data-id="element-620">
              <SendIcon size={18} data-id="element-621" />
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="bg-white min-h-full pb-16" data-id="element-622">
      {/* Header */}
      <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200" data-id="element-623">
        <div className="flex items-center" data-id="element-624">
          <Link to="/" className="text-gray-800 mr-2" data-id="element-625">
            <ArrowLeftIcon size={24} data-id="element-626" />
          </Link>
          <h1 className="text-xl font-semibold" data-id="element-627">Messages</h1>
        </div>
        <button className="text-gray-800" data-id="element-628">
          <SearchIcon size={24} data-id="element-629" />
        </button>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-gray-200" data-id="element-630">
        <button className={`flex-1 py-3 text-sm font-medium ${activeTab === 'chats' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setActiveTab('chats')} data-id="element-631">
          Chats
        </button>
        <button className={`flex-1 py-3 text-sm font-medium ${activeTab === 'requests' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500'}`} onClick={() => setActiveTab('requests')} data-id="element-632">
          Requests
          <span className="ml-1 bg-red-500 text-white text-xs px-1.5 rounded-full" data-id="element-633">
            {messageRequests.length}
          </span>
        </button>
      </div>
      {/* Contacts List */}
      <div className="divide-y divide-gray-100" data-id="element-634">
        {activeTab === 'chats' ? contacts.map(contact => <button key={contact.id} className="flex items-center p-4 w-full text-left hover:bg-gray-50" onClick={() => setActiveChat(contact.id)} data-id="element-635">
                <div className="relative" data-id="element-636">
                  <Avatar src={contact.avatar} size="md" sport={contact.sport} data-id="element-637" />
                  {contact.online && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-638"></div>}
                </div>
                <div className="ml-3 flex-1" data-id="element-639">
                  <div className="flex justify-between items-start" data-id="element-640">
                    <div data-id="element-641">
                      <div className="flex items-center" data-id="element-642">
                        <span className="font-semibold text-sm" data-id="element-643">
                          {contact.name}
                        </span>
                        {contact.verified && <svg className="w-3 h-3 text-blue-500 ml-1" fill="currentColor" viewBox="0 0 20 20" data-id="element-644">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-645"></path>
                          </svg>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-646">
                        {contact.lastMessage.length > 40 ? `${contact.lastMessage.substring(0, 40)}...` : contact.lastMessage}
                      </p>
                    </div>
                    <div className="flex flex-col items-end" data-id="element-647">
                      <span className="text-xs text-gray-500" data-id="element-648">
                        {contact.lastMessageTime}
                      </span>
                      {contact.unreadCount > 0 && <span className="mt-1 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-id="element-649">
                          {contact.unreadCount}
                        </span>}
                    </div>
                  </div>
                </div>
              </button>) : messageRequests.map(request => <button key={request.id} className="flex items-center p-4 w-full text-left hover:bg-gray-50" onClick={() => setActiveChat(request.id)} data-id="element-650">
                <Avatar src={request.avatar} size="md" sport={request.sport} data-id="element-651" />
                <div className="ml-3 flex-1" data-id="element-652">
                  <div className="flex justify-between items-start" data-id="element-653">
                    <div data-id="element-654">
                      <div className="flex items-center" data-id="element-655">
                        <span className="font-semibold text-sm" data-id="element-656">
                          {request.name}
                        </span>
                        {request.verified && <svg className="w-3 h-3 text-blue-500 ml-1" fill="currentColor" viewBox="0 0 20 20" data-id="element-657">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" data-id="element-658"></path>
                          </svg>}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5" data-id="element-659">
                        {request.lastMessage.length > 40 ? `${request.lastMessage.substring(0, 40)}...` : request.lastMessage}
                      </p>
                    </div>
                    <div className="flex flex-col items-end" data-id="element-660">
                      <span className="text-xs text-gray-500" data-id="element-661">
                        {request.lastMessageTime}
                      </span>
                      {request.unreadCount > 0 && <span className="mt-1 bg-blue-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-id="element-662">
                          {request.unreadCount}
                        </span>}
                    </div>
                  </div>
                </div>
              </button>)}
      </div>
    </div>;
};
export default ChatPage;